import styled, { css } from 'styled-components';

type PanelArea = 'feed' | 'pressure' | 'automata' | 'activity' | 'acceleration';

const panelSurface = css`
  position: relative;
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid
    ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.28)' : 'rgba(0, 0, 0, 0.2)')};
  border-radius: 14px;
  background: ${({ theme }) =>
    theme.name === 'short'
      ? 'linear-gradient(180deg, rgba(255, 255, 255, 0.11) 0%, rgba(255, 255, 255, 0.04) 100%)'
      : 'linear-gradient(180deg, rgba(0, 0, 0, 0.09) 0%, rgba(0, 0, 0, 0.03) 100%)'};
  box-shadow:
    inset 0 1px 0 ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.1)')},
    0 14px 30px ${({ theme }) => (theme.name === 'short' ? 'rgba(0, 0, 0, 0.24)' : 'rgba(0, 0, 0, 0.1)')};
  backdrop-filter: blur(4px);
  overflow: hidden;
`;

export const TerminalShell = styled.main`
  position: relative;
  isolation: isolate;
  padding: 0 20px 20px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -2;
    background:
      radial-gradient(
        1200px 420px at 15% -10%,
        ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.11)' : 'rgba(0, 0, 0, 0.09)')},
        transparent 58%
      ),
      radial-gradient(
        900px 420px at 92% 8%,
        ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.09)' : 'rgba(0, 0, 0, 0.08)')},
        transparent 62%
      );
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    z-index: -1;
    background-image:
      linear-gradient(
        to right,
        ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)')} 1px,
        transparent 1px
      ),
      linear-gradient(
        to bottom,
        ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(0, 0, 0, 0.03)')} 1px,
        transparent 1px
      );
    background-size: 26px 26px;
    opacity: 0.8;
    pointer-events: none;
  }
`;

export const TerminalGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.22fr) minmax(0, 1fr) minmax(0, 1fr);
  grid-template-rows: repeat(2, minmax(0, 1fr));
  grid-template-areas:
    'feed pressure automata'
    'feed activity acceleration';
  gap: 16px;
  width: 100%;
  height: calc(90vh - 76px);
  min-height: 460px;
`;

export const Panel = styled.section<{ $area: PanelArea }>`
  ${panelSurface};
  grid-area: ${({ $area }) => $area};
`;

export const PanelHeader = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.8rem;
  padding: 12px 14px 10px;
  border-bottom: 1px solid
    ${({ theme }) => (theme.name === 'short' ? 'rgba(255, 255, 255, 0.14)' : 'rgba(0, 0, 0, 0.1)')};
  background: ${({ theme }) =>
    theme.name === 'short' ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.04)'};
`;

export const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const PanelKicker = styled.span`
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  text-transform: uppercase;
  font-size: 10px;
  letter-spacing: 0.14em;
  opacity: 0.56;
`;

export const PanelTitle = styled.h3`
  margin: 0;
  font-size: 14px;
  letter-spacing: 0.02em;
  line-height: 1.2;
  font-weight: 600;
`;

export const PanelMeta = styled.span`
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New',
    monospace;
  font-size: 10px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.65;
  white-space: nowrap;
  padding-top: 2px;
`;

export const PanelBody = styled.div<{ $flush?: boolean }>`
  flex: 1 1 auto;
  min-height: 0;
  padding: ${({ $flush }) => ($flush ? '0' : '10px 12px 12px')};
  overflow: hidden;
`;

export const BodyFrame = styled.div`
  width: 100%;
  height: 100%;
  min-height: 0;
`;
